<script lang="ts">
  import { Control, FormControl, setupControl } from "../../forms/controls";
  import Button from "../../forms/Button.svelte";
  import FormField from "../../forms/FormField.svelte";
  import InputNumberSpinner from "../../forms/InputNumberSpinner.svelte";
  import InputArray, { ArrayControl } from "../../forms/InputArray.svelte";
  import InputGroup from "../../forms/InputGroup.svelte";
  import { Validator, type ValidatorMessages, type ValidatorResult } from "../../forms/validators";
  import type { ControlValidator } from "../../forms/validators";
  import Overlay from "../../overlay/Overlay.svelte";
  import type { RoomOccupancy } from "../services/formModels";
  import InputControlErrors from "../../forms/InputControlErrors.svelte";
  import InputSelect from "../../forms/InputSelect.svelte";
  import Tooltip from "./Tooltip.svelte";
  import type { Localizer } from "../../l10n/localization.service";

  // either set name and validators
  export let name: string = null;
  export let validators: ControlValidator[] = [];
  export let value: any[] = undefined;
  // ... or bind a control
  export let control: ArrayControl<FormControl<RoomOccupancy>> = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;

  export let T: Localizer;

  function roomsValidator(control: ArrayControl<FormControl<RoomOccupancy>>): ValidatorResult {
    const rooms = Array.isArray(control.value) ? control.value : [];

    if (rooms.length === 0) {
      return {
        validator: "roomsValidator",
        message: T.get("travelData.rooms.validation.roomsMissing"),
      };
    } else if (rooms.length === 1 && rooms.filter((_) => _?.adults > 0).length < rooms.length) {
      return {
        validator: "roomsValidator",
        message: T.get("travelData.rooms.validation.adultsMissing"),
      };
    } else if (rooms.filter((_) => _?.adults > 0).length < rooms.length) {
      return {
        validator: "roomsValidator",
        message: T.get("travelData.rooms.validation.roomsWithoutAdults"),
      };
    } else if (rooms.flatMap((_) => _?.childrenAges || []).filter((age) => isNaN(age) || (age as any) === "").length > 0) {
      return {
        validator: "roomsValidator",
        message: T.get("travelData.rooms.validation.childAgeMissing"),
      };
    }
  }

  const changes = setupControl(ArrayControl as any, {
    name,
    validators: [...validators, roomsValidator],
    value,
    control,
    standalone,
    submitEvent: null,
    configure: (ctrl) => {
      ctrl.createControl = (value) => new FormControl("room", null, value);
    },
  });
  $: ctrl = $changes;

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = value;
  }

  let openPanel = false;
  function handleClick() {
    if (ctrl.array.length < 1) {
      ctrl.array.add(new FormControl<RoomOccupancy>("room"));
    }
    openPanel = true;
  }
  function handleKeydown(e: KeyboardEvent) {
    if (e.code == "Space" || e.code == "Enter") handleClick();
  }

  function handleClose() {
    ctrl.touch();
    ctrl.validate(true);
  }

  function increment(control: FormControl<RoomOccupancy>) {
    const { array } = control.get<ArrayControl<Control<string>>>("childrenAges");
    array.add("");
  }

  function decrement(control: FormControl<RoomOccupancy>) {
    const { array } = control.get<ArrayControl<Control<string>>>("childrenAges");
    array.remove(array.pop());
  }

  let input: HTMLDivElement;

  $: rooms = ctrl.value || [];
  $: adults = rooms.reduce((c, n) => c + (n?.adults || 0), 0);
  $: children = rooms.reduce((c, n) => c + (n?.children || 0), 0);
</script>

<FormField bind:control={ctrl} {displayName} {errors} {standalone} on:change>
  <div class="input-control input-rooms" bind:this={input} class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} on:click={handleClick} on:keydown={(e) => handleKeydown(e)} tabindex="0">
    <div><span>{T.get("travelData.rooms.adults")} *</span> {adults}</div>
    <div><span>{T.get("travelData.rooms.children")}</span> {children}</div>
    <div><span>{T.get("travelData.rooms.rooms")}</span> {rooms.length}</div>
  </div>
</FormField>

<Overlay {input} bind:open={openPanel} let:overlay on:close={() => handleClose()}>
  <InputArray control={ctrl} let:array standalone on:submit={(_) => overlay.close()}>
    <div class="occupancy-rooms">
      <div class="rooms">
        {#each array as control, i (control)}
          <InputGroup {control}>
            <div class="room">
              <div class="room-title">
                <span>
                  <Tooltip tooltip={T.get("travelData.rooms.roomInfo")}>{T.get("travelData.rooms.room")} {i + 1}</Tooltip>
                </span>
                {#if i > 0}
                  <div class="room-remove" role="button" title={T.get("travelData.rooms.removeRoom")} on:click={(_) => array.remove(control)} tabindex="0" />
                {/if}
              </div>

              <div class="room-input">
                <InputNumberSpinner name="adults" label="{T.get('travelData.rooms.adults')} *" min={0} required />
              </div>

              <div class="room-input">
                <InputNumberSpinner name="children" label={T.get("travelData.rooms.children")} min={0} on:increment={(_) => increment(control)} on:decrement={(_) => decrement(control)} />
              </div>

              <InputArray name="childrenAges" let:array>
                {#if array.length}
                  <div>{T.get("travelData.rooms.childrenAges")}</div>
                  <div class="room-input-ages">
                    {#each array as control, i (control)}
                      <InputSelect {control} small validators={[Validator.required]} displayName={T.get("travelData.rooms.age")}>
                        <option value="">--</option>
                        {#each new Array(18).fill(0) as x, i (i)}
                          <option value={i.toString()}>{T.get("travelData.rooms.years", i)}</option>
                        {/each}
                      </InputSelect>
                    {/each}
                    <InputControlErrors {control} />
                  </div>
                {/if}
              </InputArray>
            </div>
          </InputGroup>
        {/each}
      </div>

      <div class="rooms-add">
        <Button
          secondary
          small
          on:click={() => {
            array.add();
            setTimeout(() => overlay.update(), 60);
          }}>+ {T.get("travelData.rooms.addRoom")}</Button
        >
      </div>
    </div>
    <!-- <InputControlErrors control={ctrl} /> -->
    <div class="overlay-actions">
      <Button on:click={(_) => ctrl.submit()}>{T.get("travelData.rooms.done")}</Button>
    </div>
  </InputArray>
</Overlay>

<style lang="scss" global>
  .input-rooms {
    display: flex;
    justify-content: stretch;
    cursor: pointer;

    > div {
      flex: 1 1 auto;
      text-align: center;
      padding: 0 0.5em;
      font-size: 14px;
      font-weight: bold;

      span {
        display: block;
        font-weight: normal;
        color: rgba(#000, 0.8);
      }
    }

    > div + div {
      border-left: solid 1px rgba(#000, 0.3);
    }
  }

  .occupancy-rooms {
    > .room-input {
      margin-bottom: 0.5em;
    }

    .room-input-ages {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-flow: row wrap;
      gap: 1rem;
      max-width: 300px;
      margin-top: 0.2em;
      margin-bottom: 0.5em;
    }

    .rooms {
      .room {
        margin-bottom: 1.2rem;
      }

      .room + .room {
        border-top: solid 2px #d9d9d9;
        padding-top: 1.2rem;
      }
    }

    .rooms-add {
      display: flex;
      justify-content: center;
      margin-top: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .room-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: bold;
      margin-bottom: 1em;
    }

    .room-remove {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 50%;
      transition: all ease 200ms;

      &:hover,
      &:focus {
        background-color: rgb(#000, 0.06);
      }

      &:after {
        content: "";
        display: inline-block;
        width: 0.9em;
        height: 0.9em;
        $close-color: #000;
        background: linear-gradient(45deg, transparent 0%, transparent 46%, $close-color 47%, $close-color 53%, transparent 54%, transparent 100%), linear-gradient(-45deg, transparent 0%, transparent 46%, $close-color 47%, $close-color 53%, transparent 54%, transparent 100%);
        vertical-align: middle;
      }
    }
  }

  .room-input {
    margin-bottom: 1em;
  }

  .overlay-actions {
    display: flex;
    justify-content: flex-end;
  }
</style>
